"use client";

// MovedIn — "here's what moved in" (MOVING-IN-PLAN.md §7, F.moveIn).
//
// John, 2026-10-24: "you can sort of get a very summarized splash screen as well
// after that: these are all the things that got ingested into your innernet.
// these are the different projects that got added. we don't need to take a
// deeper dive, meaning we can let people explore." One quiet ledger of a run
// (/api/intake/runs?id=): about you · your projects · how your ais should work ·
// people in your life · netti's questions. Each row is a door to the place it
// names; nothing here asks anything of the person.
//
// Two seats, one body: MovedInView is the import sheet's last step (it takes the
// sheet's body, never a second sheet: law_modal_quiet), and MovedInSheet is the
// same view in THE sheet, opened from the link an ai hands back
// (/dashboard?moved=<run>) or from the memory space's list of past move-ins.

import { useEffect, useId, useState } from "react";
import EtherModal from "../ether/EtherModal";
import CopyBlock from "../CopyBlock";
import { PillButton } from "../ui";
import { AGENTS_PANEL_VIEW_KEY } from "../agents/agentViews";
import type { IntakeRun } from "../../lib/intake/runs";
import { PART_WORDS } from "./words";
import { MOVED_CHANGED_EVENT, movedChanged } from "../../lib/intake/open";
import { ReviewBody, forgetReview, useReview } from "./Review";
import "./moved-in.css";

export interface MovedInProject {
  id: string;
  slug: string;
  name: string;
  color: string | null;
  icon: string | null;
  new: boolean;
}

type Read = { run: IntakeRun; projects: MovedInProject[] } | "loading" | "missing";

const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const plural = (k: number, one: string, many = `${one}s`) => `${k.toLocaleString("en-US")} ${k === 1 ? one : many}`;

export { PART_WORDS } from "./words";

/** "just now" · "today" · "on 3 oct" */
export function whenWords(iso: string | null | undefined, now = Date.now()): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "";
  const mins = (now - t) / 60_000;
  if (mins < 10) return "just now";
  const d = new Date(t);
  if (new Date(now).toDateString() === d.toDateString()) return "today";
  return `on ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toLowerCase()}`;
}

/** the line under the sheet's title */
export function runMeta(run: Pick<IntakeRun, "source" | "lane" | "started_at" | "status"> & Pick<Partial<IntakeRun>, "stage">, now = Date.now()): string {
  const from =
    run.lane === "sync"
      ? `folded from ${run.source ?? "your ais"}`
      : run.lane === "ai"
      ? run.source
        ? `from ${run.source}, over innernet`
        : "from your ai, over innernet"
      : run.lane === "files"
        ? "from your files and notes"
        : run.source
          ? `from ${run.source}`
          : "";
  const when = whenWords(run.started_at, now);
  // a staged run is read through and waiting for the person, not still arriving
  const still = run.status === "open" && run.stage !== "staged" ? " · still coming in" : "";
  return [from, when].filter(Boolean).join(" · ") + still;
}

/** what the person's facts were mostly about, in a few words */
export function partsLine(byDimension: Record<string, number> | undefined): string {
  const top = Object.entries(byDimension ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([d]) => PART_WORDS[d] ?? d.replace(/_/g, " "));
  return top.join(" · ");
}

/** inside the dashboard's shell (its top bar is on the page: the dashboard, or /mirror), a door moves the shell; anywhere else it is a link */
function inShell(): boolean {
  return typeof document !== "undefined" && !!document.querySelector(".os-topbar");
}
function goShell(path: string, view?: string): void {
  window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path, ...(view ? { view } : {}) } }));
}
/** the agents space's identities view (2026-10-25: identities live with the agents): the view rides the move, and is
 *  left for a space not yet mounted, as settings hands it over */
function goIdentity(): void {
  try {
    sessionStorage.setItem(AGENTS_PANEL_VIEW_KEY, "identities");
  } catch {
    /* private mode: the move carries the view */
  }
  goShell("/agents", "identities");
  window.setTimeout(() => window.dispatchEvent(new CustomEvent("innernet:panel-view", { detail: { panel: "agents", view: "identities" } })), 160);
}

function Door({ href, onGo, children, onLeave }: { href: string; onGo: () => void; children: React.ReactNode; onLeave?: () => void }) {
  return (
    <a
      className="quiet-link mi-door"
      href={href}
      onClick={(e) => {
        if (!inShell()) return;
        e.preventDefault();
        onLeave?.();
        onGo();
      }}
    >
      {children}
    </a>
  );
}

/** Read one run (and its projects' names). Polls while it is still coming in. */
export function useMovedIn(runId: string | null): Read {
  const [read, setRead] = useState<Read>("loading");
  useEffect(() => {
    if (!runId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // another run: never the last one's numbers while this one is read
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRead("loading");
    const pull = async () => {
      try {
        const r = await fetch(`/api/intake/runs?id=${encodeURIComponent(runId)}`, { cache: "no-store" });
        if (!alive) return;
        // only "not here" is missing; anything else (a slow server, a dropped line) is asked again
        if (r.status === 404) return setRead("missing");
        if (!r.ok) {
          timer = setTimeout(pull, 8000);
          return;
        }
        const j = (await r.json()) as { run: IntakeRun; projects: MovedInProject[] };
        setRead({ run: j.run, projects: j.projects ?? [] });
        // still arriving: asked again soon. a staged run waits on the person, so it is read again when they act
        // still arriving, or being kept by netti: asked again soon. a staged run waits on the person, so it is read again when they act
        if (j.run.status === "open" && (j.run.stage !== "staged" || j.run.keep_at)) timer = setTimeout(pull, j.run.keep_at ? 10_000 : 5000);
      } catch {
        if (alive) timer = setTimeout(pull, 8000);
      }
    };
    void pull();
    // kept, let go or taken back (anywhere): read it again, keeping what is shown until the answer is in
    const changed = (e: Event) => {
      if ((e as CustomEvent<{ run?: string }>).detail?.run !== runId) return;
      if (timer) clearTimeout(timer);
      void pull();
    };
    window.addEventListener(MOVED_CHANGED_EVENT, changed);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      window.removeEventListener(MOVED_CHANGED_EVENT, changed);
    };
  }, [runId]);
  return runId ? read : "missing";
}

/** The ledger of one run. `onLeave` is told before a door moves the shell (the sheet closes first). */
export function MovedInView({ read, onLeave }: { read: Read; onLeave?: () => void }) {
  if (read === "loading") {
    return (
      <div className="mi" aria-busy="true">
        <span className="skeleton-line" style={{ width: "72%" }} />
        <span className="skeleton-line" style={{ width: "58%" }} />
        <span className="skeleton-line" style={{ width: "64%" }} />
      </div>
    );
  }
  if (read === "missing") return <p className="cm-sub">this move-in is not here any more.</p>;

  const { run, projects } = read;
  const c = run.counts ?? {};
  const facts = n(c.facts);
  const known = n(c.known);
  const others = n(c.about_others);
  const lines = n(c.agent_lines);
  const asFacts = n(c.agent_as_facts);
  const unplaced = n(c.agent_unplaced);
  const questions = n(c.questions);
  const queued = n(c.queued);
  const fresh = projects.filter((p) => p.new);
  const fed = projects.filter((p) => !p.new);
  const nothing = !facts && !known && !others && !lines && !asFacts && !unplaced && !questions && !queued && projects.length === 0;

  const keeping = !!run.keep_at && (run.stage === "staged" || run.stage === "applying");
  return (
    <div className="mi">
      {keeping && <p className="cm-sub mi-keeping" aria-live="polite">netti is keeping the rest of it now. it fills in here as it lands.</p>}
      {nothing && keeping ? null : nothing ? (
        <p className="cm-sub">
          {run.status === "open" ? "it is still coming in. this fills as it lands." : "nothing new came of it: what it held was already here, or it held nothing netti could keep."}
        </p>
      ) : (
        <div className="detail-rows mi-rows">
          {(facts > 0 || known > 0 || queued > 0) && (
            <div className="detail-row mi-row">
              <span>about you</span>
              <span>
                {[facts ? plural(facts, "fact") : "", known ? `${known.toLocaleString("en-US")} already known` : "", queued ? `${queued.toLocaleString("en-US")} still being read` : ""]
                  .filter(Boolean)
                  .join(" · ")}
                {partsLine(c.by_dimension) && <span className="mi-sub">{partsLine(c.by_dimension)}</span>}
                <Door href="/dashboard/you" onGo={() => goShell("/you")} onLeave={onLeave}>see them</Door>
              </span>
            </div>
          )}
          {projects.length > 0 && (
            <div className="detail-row mi-row">
              <span>your projects</span>
              <span>
                {[fresh.length ? `${fresh.length} new` : "", fed.length ? `${fed.length} added to` : ""].filter(Boolean).join(" · ")}
                <span className="mi-projects">
                  {[...fresh, ...fed].slice(0, 8).map((p) => (
                    <Door key={p.id} href={`/dashboard?open=${encodeURIComponent(`/projects/${p.id}`)}`} onGo={() => goShell(`/projects/${p.id}`)} onLeave={onLeave}>
                      {p.icon ? `${p.icon} ` : ""}
                      {p.name}
                      {p.new ? <span className="mi-new"> · new</span> : null}
                    </Door>
                  ))}
                  {projects.length > 8 && <span className="mi-sub">and {(projects.length - 8).toLocaleString("en-US")} more</span>}
                </span>
              </span>
            </div>
          )}
          {(lines > 0 || asFacts > 0 || unplaced > 0) && (
            <div className="detail-row mi-row">
              <span>how your ais should work</span>
              <span>
                {lines > 0 ? `${plural(lines, "line")} waiting for your yes` : asFacts > 0 ? `${plural(asFacts, "line")} kept as yours` : "none waiting yet"}
                {lines > 0 && (
                  <Door href="/dashboard/agents?v=identities" onGo={goIdentity} onLeave={onLeave}>
                    look at them
                  </Door>
                )}
                {unplaced > 0 && (
                  <span className="mi-sub">
                    {plural(unplaced, "more line")} did not fit: your ai already has as many waiting as it holds. keep or let go of those first, then bring these in again.
                  </span>
                )}
              </span>
            </div>
          )}
          {others > 0 && (
            <div className="detail-row mi-row">
              <span>people in your life</span>
              <span>
                {plural(others, "fact")} kept as theirs
                <span className="mi-sub">never read as yours</span>
              </span>
            </div>
          )}
          {questions > 0 && (
            <div className="detail-row mi-row">
              <span>netti&apos;s questions</span>
              <span>
                {plural(questions, "question")} for when you like
                <Door href="/dashboard/you" onGo={() => goShell("/you")} onLeave={onLeave}>answer them</Door>
              </span>
            </div>
          )}
        </div>
      )}
      {n(c.failed) > 0 && <p className="cm-where"><span aria-hidden>↳</span> {plural(n(c.failed), "part")} could not be read just now. bringing it in again picks them up.</p>}
    </div>
  );
}

// ── each thing it wrote, and taking it back (lib/intake/written · lib/intake/undo) ──

interface Detail {
  facts: Array<{ id: string; statement: string; dimension: string; disclosure: string; voice: string | null; about: boolean; current: boolean; over: boolean; verbatim: string | null; read: string | null }>;
  notes: Array<{ hash: string; project_id: string; project: string; text: string; folded: boolean; discarded: boolean }>;
  lines: Array<{ id: string; for: string; text: string; waiting: boolean }>;
  reads: Array<{ id: string; app: string | null; at: string | null; note: string; words: string[]; aside: string | null }>;
}

/** what one note said, in both voices: the ai's note, and the person's own words apart (THE code block) */
function ReadBox({ read }: { read: Detail["reads"][number] }) {
  return (
    <div className="mi-read">
      {read.words.map((w, i) => (
        <div key={i} className="mi-read__part">
          <span className="mi-read__who">your words</span>
          <CopyBlock text={w} className="mi-read__box copy-block--soft" />
        </div>
      ))}
      {read.note && (
        <div className="mi-read__part">
          <span className="mi-read__who">{read.app ? `${read.app}'s note` : "the ai's note"}</span>
          <CopyBlock text={read.note} className="mi-read__box copy-block--soft" />
        </div>
      )}
    </div>
  );
}

const CLASS_SHORT: Record<string, string> = { private: "private", self: "just you", trusted: "people you trust", work: "work", public: "public" };

/** Every thing a run wrote, as it stands now, each with a way to take it back. Read when asked for. */
export function WrittenList({ runId, onChanged, auto = false }: { runId: string; onChanged?: () => void; auto?: boolean }) {
  const [detail, setDetail] = useState<Detail | null | "loading">(null);
  const [open, setOpen] = useState<string | null>(null);
  const [said, setSaid] = useState("");
  const [allTwice, setAllTwice] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setDetail("loading");
    try {
      const r = await fetch(`/api/intake/runs?id=${encodeURIComponent(runId)}&detail=1`, { cache: "no-store" });
      const j = (await r.json().catch(() => ({}))) as { detail?: Detail };
      setDetail(j.detail ?? { facts: [], notes: [], lines: [], reads: [] });
    } catch {
      setDetail({ facts: [], notes: [], lines: [], reads: [] });
    }
  };
  // a fold opens on what it read and wrote: there is nothing to ask for first
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (auto) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, runId]);
  const takeBack = async (only?: string[]) => {
    setBusy(true);
    try {
      // a press that does not get through is sent again on its own; only then is anything said
      let r: Response | null = null;
      for (let tries = 0; tries < 3 && !r?.ok; tries++) {
        if (tries) await new Promise((ok) => setTimeout(ok, tries * 3_000));
        r = await fetch("/api/intake/undo", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ run: runId, ...(only ? { only } : {}) }) }).catch(() => null);
        if (r && r.status === 404) break;
      }
      const j = r ? ((await r.json().catch(() => ({}))) as { facts?: number; notes?: number; lines?: number; questions?: number; left?: string[]; still?: number; error?: string }) : {};
      if (!r?.ok) setSaid(j.error ?? "innernet could not be reached just now. please take it back again in a moment.");
      else {
        const n = (j.facts ?? 0) + (j.notes ?? 0) + (j.lines ?? 0) + (j.questions ?? 0);
        const still = j.still ?? 0;
        setSaid(
          [
            n ? `${n === 1 ? "one thing" : `${n} things`} taken back.` : still ? "" : "nothing more could be taken back.",
            // what netti could not reach this moment it takes back on its own (the heal sweep)
            still ? `netti is taking the rest back${n ? "" : " now"}.` : "",
            ...(j.left ?? []),
          ]
            .filter(Boolean)
            .join(" "),
        );
        await load();
        onChanged?.();
        movedChanged(runId);
      }
    } finally {
      setBusy(false);
      setAllTwice(false);
    }
  };
  if (!detail) {
    return (
      <p className="mi-more">
        <button type="button" className="quiet-link" onClick={() => void load()}>see each one</button>
      </p>
    );
  }
  if (detail === "loading") return <p className="mi-more mi-sub">reading what it wrote…</p>;
  const mine = detail.facts.filter((f) => !f.about);
  const theirs = detail.facts.filter((f) => f.about);
  const readOf = new Map(detail.reads.map((r) => [r.id, r]));
  const row = (key: string, text: string, meta: string, live: boolean, canTake: boolean, id: string, from?: { read: Detail["reads"][number] | undefined; verbatim: string | null }) => (
    <li key={key} className="mi-item" data-off={!live || undefined}>
      <p className="mi-item__text">{text || "a line for your ais"}</p>
      <span className="mi-item__meta">
        {meta}
        {from && (from.read || from.verbatim) ? (
          <button type="button" className="quiet-link" aria-expanded={open === key} onClick={() => setOpen(open === key ? null : key)}>
            {open === key ? "fold it away" : "what it was read from"}
          </button>
        ) : null}
        {live && canTake ? (
          <button type="button" className="quiet-link" disabled={busy} onClick={() => void takeBack([id])}>take it back</button>
        ) : null}
      </span>
      {open === key && from && (from.read ? <ReadBox read={from.read} /> : from.verbatim ? <CopyBlock text={from.verbatim} className="mi-read__box copy-block--soft" /> : null)}
    </li>
  );
  const fromOf = (f: Detail["facts"][number]) => ({ read: f.read ? readOf.get(f.read) : undefined, verbatim: f.verbatim });
  const setAside = detail.reads.filter((r) => r.aside);
  const nothing = !detail.facts.length && !detail.notes.length && !detail.lines.length;
  return (
    <div className="mi-written">
      {nothing && <p className="mi-sub">nothing it wrote is left to show.</p>}
      {mine.length > 0 && (
        <section aria-label="about you">
          <p className="mi-written__head">about you</p>
          <ul className="mi-items">{mine.map((f) => row(f.id, f.statement, `${PART_WORDS[f.dimension] ?? f.dimension.replace(/_/g, " ")} · ${CLASS_SHORT[f.disclosure] ?? f.disclosure}${f.voice === "said" ? " · your words" : ""}${!f.current ? " · taken back" : f.over ? " · reworded one you had" : ""}`, f.current, !f.over, f.id, fromOf(f)))}</ul>
        </section>
      )}
      {theirs.length > 0 && (
        <section aria-label="people in your life">
          <p className="mi-written__head">people in your life</p>
          <ul className="mi-items">{theirs.map((f) => row(f.id, f.statement, `kept as theirs${!f.current ? " · taken back" : ""}`, f.current, !f.over, f.id, fromOf(f)))}</ul>
        </section>
      )}
      {detail.notes.length > 0 && (
        <section aria-label="your projects">
          <p className="mi-written__head">your projects</p>
          <ul className="mi-items">
            {detail.notes.map((n) => row(n.hash, n.text, `${n.project}${n.discarded ? " · taken back" : n.folded ? " · folded in" : " · waiting for the fold"}`, !n.discarded, !n.folded, n.hash))}
          </ul>
        </section>
      )}
      {detail.lines.length > 0 && (
        <section aria-label="how your ais should work">
          <p className="mi-written__head">how your ais should work</p>
          <ul className="mi-items">{detail.lines.map((l) => row(l.id, l.text, `${l.for || "every ai"}${l.waiting ? " · waiting for your yes" : " · kept or let go by you"}`, l.waiting, true, l.id))}</ul>
        </section>
      )}
      {setAside.length > 0 && (
        <section aria-label="what netti set aside">
          <p className="mi-written__head">what netti read and set aside</p>
          <ul className="mi-items">
            {setAside.map((r) => (
              <li key={r.id} className="mi-item">
                <p className="mi-item__text">{r.note.split("\n")[0].slice(0, 180) || "a note"}</p>
                <span className="mi-item__meta">
                  {r.aside}
                  <button type="button" className="quiet-link" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
                    {open === r.id ? "fold it away" : "read it"}
                  </button>
                </span>
                {open === r.id && <ReadBox read={r} />}
              </li>
            ))}
          </ul>
        </section>
      )}
      {said && <p className="cm-where" aria-live="polite"><span aria-hidden>↳</span> {said}</p>}
      {!nothing && (
        <p className="mi-more">
          <button type="button" className="quiet-link" disabled={busy} onClick={() => (allTwice ? void takeBack() : setAllTwice(true))}>
            {allTwice ? "yes, take all of it back" : "take all of it back"}
          </button>
        </p>
      )}
    </div>
  );
}

/** The same view in THE sheet: from the link an ai hands back, or from a past move-in. A staged one opens as its review. */
export function MovedInSheet({ runId, onClose }: { runId: string | null; onClose: () => void }) {
  const titleId = useId();
  const read = useMovedIn(runId);
  // a run the person already pressed keep on is netti's to finish: the sheet shows what moved in, not the review
  const staged = typeof read === "object" && read.run.stage === "staged" && !read.run.keep_at;
  const review = useReview(staged ? runId : null);
  // the run a press just kept: until the run is read again, the sheet waits rather than showing it as it was
  const [keptRun, setKeptRun] = useState<string | null>(null);
  const kept = keptRun != null && keptRun === runId;
  const settling = kept && staged;
  const meta = typeof read === "object" ? runMeta(read.run) : "";
  return (
    <EtherModal
      open={!!runId}
      onClose={onClose}
      labelledBy={titleId}
      size="md"
      height="tall"
      className="mi-sheet"
      nav={{ crumb: [{ label: "your memory", onClick: onClose }, { label: staged && !kept ? "waiting for your look" : "what moved in" }] }}
      header={
        <>
          <h2 id={titleId} className="eth-title">
            {staged && !kept ? "here's what netti would keep" : typeof read === "object" && read.run.lane === "sync" ? "what netti folded" : "here's what moved in"}
          </h2>
          {meta && <p className="eth-meta">{staged && !kept ? `${meta} · nothing is written until you say so` : meta}</p>}
        </>
      }
      footer={
        staged && !kept ? (
          <PillButton
            disabled={typeof review.plan !== "object" || review.kept === 0 || review.busy}
            onClick={async () => {
              const id = runId;
              if (id && (await review.apply())) {
                forgetReview(id);
                setKeptRun(id);
              }
            }}
          >
            {review.busy ? "keeping it…" : `keep ${review.kept} of ${review.total}`}
          </PillButton>
        ) : (
          <PillButton
            onClick={() => {
              onClose();
              if (inShell()) goShell("/projects");
            }}
          >
            explore
          </PillButton>
        )
      }
    >
      {staged && !kept ? (
        <ReviewBody review={review} />
      ) : (
        <>
          <MovedInView read={settling ? "loading" : read} onLeave={onClose} />
          {typeof read === "object" && read.run.stage !== "staged" && read.run.status !== "open" && <WrittenList runId={read.run.id} auto={read.run.lane === "sync"} />}
        </>
      )}
    </EtherModal>
  );
}
